// Client-side capacity schedule summary derivation.
//
// Keep this aligned with server/lib/locationCapacitySchedule.js:
// rows on or before today determine Today, strictly future rows determine
// Next change, and only the canonical exit date is the closing row.

function toDateString(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }
  const candidate = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(candidate) ? candidate : null;
}

export function normalizeScheduleDate(value) {
  return toDateString(value);
}

function compareRows(a, b) {
  const aDate = toDateString(a.effectiveDate);
  const bDate = toDateString(b.effectiveDate);
  if (!aDate) return bDate ? 1 : 0;
  if (!bDate) return -1;
  return aDate.localeCompare(bDate);
}

/**
 * Derive the capacity summary from the rows currently displayed in the form.
 *
 * @param {Array<object>} rows
 * @param {Date|string} [asOf=new Date()] UTC date used for the today/future gate
 * @returns {{onlineDate: string|null, todaySeats: number, nextChange: object|null, closeRow: object|null}}
 */
export function deriveCapacityScheduleSummary(
  rows = [],
  asOf = new Date(),
  exitDate = null,
  onlineDate = null,
  reopenDate = null,
) {
  const orderedRows = [...(Array.isArray(rows) ? rows : [])].sort(compareRows);
  const today = toDateString(asOf) ?? new Date().toISOString().slice(0, 10);
  const effectiveRows = orderedRows.filter((row) => {
    const date = toDateString(row.effectiveDate);
    return date && date <= today;
  });
  const futureRows = orderedRows.filter((row) => {
    const date = toDateString(row.effectiveDate);
    return date && date > today;
  });
  const firstRow = orderedRows[0] ?? null;
  const canonicalExit = toDateString(exitDate);
  const canonicalOnline = toDateString(onlineDate);
  const canonicalReopen = toDateString(reopenDate);
  const beforeOnline = canonicalOnline != null && canonicalOnline > today;
  const inClosedInterval = canonicalExit != null
    && canonicalExit <= today
    && (canonicalReopen == null || canonicalReopen > today);
  const closeRow = canonicalExit
    ? (orderedRows.find((row) => toDateString(row.effectiveDate) === canonicalExit) ?? {
        id: "preview-lifecycle-exit",
        effectiveDate: canonicalExit,
        installedSeats: 0,
        maxDensity: 0,
        source: "lifecycle_sync",
      })
    : null;

  return {
    onlineDate: canonicalOnline
      ?? (firstRow ? toDateString(firstRow.effectiveDate) : null),
    todaySeats: beforeOnline || inClosedInterval
      ? 0
      : effectiveRows.length > 0
      ? Number(effectiveRows[effectiveRows.length - 1].installedSeats)
      : 0,
    nextChange: futureRows[0] ?? null,
    closeRow,
  };
}

/**
 * Overlay the managed lifecycle boundary rows for an unsaved exit/reopen
 * draft. This mirrors syncLifecycleScheduleRows on the server: manual and
 * imported rows remain untouched, while the existing lifecycle rows are
 * replaced by the rows implied by the current draft.
 */
export function deriveLifecyclePreviewRows(rows = [], exitDate, reopenDate) {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const manualRows = sourceRows.filter((row) => row?.source !== "lifecycle_sync");
  const exit = toDateString(exitDate);
  const reopen = toDateString(reopenDate);
  if (!exit) return manualRows;

  const previewRows = [...manualRows];
  const hasDate = (date) => previewRows.some(
    (row) => toDateString(row?.effectiveDate) === date,
  );

  if (!hasDate(exit)) {
    previewRows.push({
      id: "preview-lifecycle-exit",
      effectiveDate: exit,
      installedSeats: 0,
      maxDensity: 0,
      source: "lifecycle_sync",
      note: "Canonical physical exit boundary",
    });
  }

  if (reopen && reopen > exit && !hasDate(reopen)) {
    const sourceRow = [...manualRows]
      .filter((row) => {
        const date = toDateString(row?.effectiveDate);
        return date && date < exit && Number(row?.installedSeats) > 0;
      })
      .sort((a, b) => toDateString(b.effectiveDate).localeCompare(toDateString(a.effectiveDate)))[0];

    if (sourceRow) {
      previewRows.push({
        id: "preview-lifecycle-reopen",
        effectiveDate: reopen,
        installedSeats: Number(sourceRow.installedSeats),
        maxDensity: sourceRow.maxDensity == null ? null : Number(sourceRow.maxDensity),
        source: "lifecycle_sync",
        note: "Canonical physical reopen boundary",
      });
    }
  }

  return previewRows;
}

export function closeDateVerb(closeRow, asOf = new Date()) {
  const closeDate = toDateString(closeRow?.effectiveDate);
  const today = toDateString(asOf) ?? new Date().toISOString().slice(0, 10);
  return closeDate && closeDate < today ? "Closed" : "Closes";
}